import {
  InstrumentSummaryDetailed,
  useInstrumentSummaryDetailed,
  useToken,
  Environment,
  SortCategoryId,
} from "@iliotech/watchlist-api";
import { InstrumentOverviewHeader } from "./components/InstrumentOverviewHeader/InstrumentOverviewHeader";
import { InstrumentPriceChart } from "./components/InstrumentPriceChart/InstrumentPriceChart";
import { InstrumentOverviewSection } from "./components/InstrumentOverviewSection/InstrumentOverviewSection";
import { Week52RangeChart } from "../../../insights/InstrumentLevel/Week52RangeChart/Week52RangeChart";
import { RsiRangesChart } from "../../../insights/InstrumentLevel/RsiRangesChart/RsiRangesChart";
import { useAllSortedItems } from "@iliotech/watchlist-api/src/hooks/useAllSortedItems";
import { VolatilityRangesChart } from "../../../insights/InstrumentLevel/VolatilityRangesChart/VolatilityRangesChart";
import { RiskReturnChart } from "../../../insights/InstrumentLevel/RiskReturnChart/RiskReturnChart";
import styles from "./InstrumentOverview.module.scss";
import { getRiskReturnOverviewChartData } from "../../../insights/InstrumentLevel/__processing/getRiskReturnOverviewChartData";
import { getWeek52RangeData } from "@widgets/insights/InstrumentLevel/__processing";
import { HorizontalBarChart } from "@widgets/main";
import { InstrumentInfo } from "@iliotech/watchlist-api";
import moment from "moment";
import React, { useState, useMemo, memo } from "react";
import { FromPeriodChooser } from "./components/FromPeriodChooser/FromPeriodChooser";
import { useSortCategory } from "@iliotech/watchlist-api/src/hooks/useSortCategory";

interface IProps {
  instrumentInfo: InstrumentInfo;
  watchlistId: string;
  instrumentId: string;
  instrumentSummaryDetailed: InstrumentSummaryDetailed;
}

const TECHNICALS_BLACKLIST = ["BOND"];
const FUNDAMENTALS_BLACKLIST = ["BOND", "CRYPTO-CURRENCY", "PORTFOLIO"];
const DEFAULT_FROM_DATE = moment().subtract(1, "year").format("YYYY-MM-DD");

// Memoized components that don't need to re-render when fromDate changes
const MemoizedInstrumentOverviewHeader = memo(
  ({ instrumentSummaryDetailed, instrumentInfo }: any) => (
    <InstrumentOverviewHeader
      instrumentSummaryDetailed={instrumentSummaryDetailed}
      instrumentInfo={{
        ...instrumentInfo,
        description: instrumentInfo.description ?? undefined,
      }}
    />
  )
);

const MemoizedInstrumentOverviewSection = memo(
  ({
    title,
    Component,
    instrumentId,
    insightId,
    watchlistId,
    sortedItems,
    transformer,
    hideTable,
    hideStats,
    collapsible,
    titleClassName,
    overrideGoodToKnow,
    children,
  }: any) => (
    <InstrumentOverviewSection
      title={title}
      Component={Component}
      instrumentId={instrumentId}
      insightId={insightId}
      watchlistId={watchlistId}
      sortedItems={sortedItems}
      transformer={transformer}
      hideTable={hideTable}
      hideStats={hideStats}
      collapsible={collapsible}
      titleClassName={titleClassName}
      overrideGoodToKnow={overrideGoodToKnow}
    >
      {children}
    </InstrumentOverviewSection>
  )
);

// PriceChartSection component that manages its own fromDate state
const PriceChartSection = memo(
  ({
    environment,
    token,
    watchlistId,
    instrumentId,
    minDate = moment().subtract(5, "years").format("YYYY-MM-DD"),
  }: {
    environment: Environment;
    token: string;
    watchlistId: string;
    instrumentId: string;
    minDate?: string;
  }) => {
    const [fromDate, setFromDate] = useState(DEFAULT_FROM_DATE);
    const [selectedMode, setSelectedMode] =
      useState<"price" | "relativePricePerformance">("price");
    const [chartType, setChartType] = useState<string>("line");
    const categoryAxisFormatter = React.useMemo(
      () => (e?: string) => {
        if (!e) return "";

        // Calculate the duration between fromDate and today
        const duration = moment().diff(moment(fromDate), "months", true);

        // If duration is more than 3 months, show MMM' YY format
        if (duration > 3) {
          return moment(e).format("MMM' YY");
        } else {
          // Otherwise show DD MMM YY format
          return moment(e).format("DD MMM YY");
        }
      },
      [fromDate] // Recalculate when fromDate changes
    );

    const tooltipCategoryAxisFormatter = (e?: string) => {
      return moment(e).format("DD MMM YY");
    };
    // Fetch the detailed instrument data for the price chart
    const { data: instrumentSummaryDetailed } = useInstrumentSummaryDetailed(
      environment,
      instrumentId,
      token,
      watchlistId,
      fromDate
    );

    return (
      <div className={styles.sectionContainer}>
        <FromPeriodChooser
          stuckPeriod={
            selectedMode === "relativePricePerformance" ? "1Y" : undefined
          }
          fromDate={fromDate}
          setFromDate={setFromDate}
          minDate={minDate}
        />
        {instrumentSummaryDetailed ? (
          <InstrumentPriceChart
            selectedMode={selectedMode}
            setSelectedMode={setSelectedMode}
            instrumentSummaryDetailed={instrumentSummaryDetailed}
            categoryAxisFormatter={categoryAxisFormatter}
            tooltipCategoryAxisFormatter={tooltipCategoryAxisFormatter}
            chartType={chartType}
            setChartType={setChartType}
            watchlistId={watchlistId}
          />
        ) : (
          <div className={styles.loadingContainer}>
            <p>Loading price data...</p>
          </div>
        )}
      </div>
    );
  }
);

export const InstrumentOverview = ({
  instrumentInfo,
  watchlistId,
  instrumentId,
  instrumentSummaryDetailed,
}: IProps) => {
  const { environment, token } = useToken();

  const instrumentDetails = instrumentSummaryDetailed?.instrument;
  const instrumentData = instrumentSummaryDetailed?.instrumentData;
  const { data: sortCategories } = useSortCategory(watchlistId, environment);
  const { mergedData: sortedItems } = useAllSortedItems({
    environment,
    watchlistId,
    sortCategories: sortCategories || [],
  });

  const transformer = (data: any) => getRiskReturnOverviewChartData(data);
  const isPortfolio = instrumentInfo.type.toUpperCase() === "PORTFOLIO";
  const hasTechnicals = !TECHNICALS_BLACKLIST.includes(
    instrumentInfo.type.toUpperCase()
  );
  const hasFundamentals = !FUNDAMENTALS_BLACKLIST.includes(
    instrumentInfo.type.toUpperCase()
  );

  // Memoize the immutable parts of the data
  const immutableData = useMemo(
    () => ({
      instrumentDetails,
      instrumentInfo,
      sortedItems,
      isPortfolio,
      hasTechnicals,
      hasFundamentals,
      instrumentData,
    }),
    [
      instrumentDetails,
      instrumentInfo,
      sortedItems,
      isPortfolio,
      hasTechnicals,
      hasFundamentals,
      instrumentData,
    ]
  );

  if (!instrumentDetails) {
    return null; // or return a loading state/error message component
  }

  return (
    <div>
      <MemoizedInstrumentOverviewHeader
        instrumentSummaryDetailed={instrumentSummaryDetailed}
        instrumentInfo={immutableData.instrumentInfo}
      />

      {/* PriceChartSection manages its own fromDate state */}
      <PriceChartSection
        minDate={instrumentData?.minDate}
        environment={environment}
        token={token}
        watchlistId={watchlistId}
        instrumentId={instrumentId}
      />

      <div className={styles.sectionContainer}>
        <MemoizedInstrumentOverviewSection
          title={"Performance"}
          Component={Week52RangeChart}
          instrumentId={immutableData.instrumentDetails?.id!}
          insightId={"INSTRUMENT_SUMMARY_PERFORMANCE"}
          watchlistId={watchlistId}
          sortedItems={immutableData.sortedItems!}
          transformer={(data: any) => {
            const builtData = {
              ...data.timeSeriesData,
              instrument: data.instrument,
            };
            return getWeek52RangeData(builtData);
          }}
        />
      </div>
      {immutableData.hasTechnicals && (
        <div className={styles.sectionContainer}>
          <MemoizedInstrumentOverviewSection
            title={
              immutableData.isPortfolio ? "Portfolio Statistics" : "Technicals"
            }
            Component={RsiRangesChart}
            instrumentId={immutableData.instrumentDetails?.id!}
            insightId={"INSTRUMENT_SUMMARY_TECHNICALS"}
            watchlistId={watchlistId}
          />
        </div>
      )}
      {immutableData.hasTechnicals && (
        <div className={styles.sectionContainer}>
          <MemoizedInstrumentOverviewSection
            title={
              immutableData.isPortfolio
                ? "Portfolio RSI Bands"
                : "Technical RSI Bands"
            }
            Component={RsiRangesChart}
            instrumentId={immutableData.instrumentDetails?.id!}
            insightId={"INSTRUMENT_BAND_SUMMARY_TECHNICALS"}
            watchlistId={watchlistId}
          />
        </div>
      )}
      <div className={styles.sectionContainer}>
        <MemoizedInstrumentOverviewSection
          title={"Risk"}
          Component={VolatilityRangesChart}
          instrumentId={immutableData.instrumentDetails?.id!}
          insightId={"INSTRUMENT_SUMMARY_RISK"}
          watchlistId={watchlistId}
          sortedItems={immutableData.sortedItems!}
        >
          <div className={styles.sectionContainer}>
            <MemoizedInstrumentOverviewSection
              hideTable={true}
              hideStats={true}
              title={"1 yr Risk Return"}
              Component={RiskReturnChart}
              instrumentId={immutableData.instrumentDetails?.id!}
              insightId={"INSTRUMENT_SUMMARY_RISK"}
              watchlistId={watchlistId}
              sortedItems={immutableData.sortedItems!}
              collapsible={false}
              titleClassName={styles.smallerTitle}
              transformer={transformer}
              overrideGoodToKnow={(data: any) => {
                const riskReturnChartData =
                  data.chart?.data.riskReturnChartData;

                if (riskReturnChartData) {
                  return riskReturnChartData.whyImportant;
                }
                return null;
              }}
            >
              <div></div>
            </MemoizedInstrumentOverviewSection>
          </div>
        </MemoizedInstrumentOverviewSection>
      </div>
      {immutableData.hasFundamentals && (
        <div className={styles.sectionContainer}>
          <MemoizedInstrumentOverviewSection
            title={"Fundamentals"}
            Component={HorizontalBarChart}
            instrumentId={immutableData.instrumentDetails?.id!}
            insightId={"INSTRUMENT_SUMMARY_FUNDAMENTALS"}
            watchlistId={watchlistId}
            sortedItems={immutableData.sortedItems!}
          />
        </div>
      )}
    </div>
  );
};
